import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';

const ImageGallery = ({ platformProductDetail }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const imagesPerPage = 10;

  const images = platformProductDetail?.image_set || [];
  const totalImages = images.length;
  const allActiveImages = useMemo(
    () => images.filter((image) => image.is_active === true),
    [images]
  );

  const startIndex = currentPage * imagesPerPage;
  const endIndex = Math.min(startIndex + imagesPerPage, totalImages);
  const currentImages = images.slice(startIndex, endIndex);
  const activeImages = currentImages.filter((image) => image.is_active === true);

  const previewImage = previewIndex !== null ? allActiveImages[previewIndex] : null;

  const getGlobalActiveIndex = (image: any) =>
    allActiveImages.findIndex(
      (item) =>
        item?.image_url === image?.image_url &&
        item?.image_position === image?.image_position
    );

  const handleNextPage = () => {
    if (endIndex < totalImages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const openPreview = (image: any) => {
    const globalIndex = getGlobalActiveIndex(image);
    if (globalIndex >= 0) {
      setPreviewIndex(globalIndex);
    }
  };

  const closePreview = () => setPreviewIndex(null);

  const showPreviousPreview = () => {
    if (previewIndex === null || allActiveImages.length <= 1) return;
    setPreviewIndex((previewIndex - 1 + allActiveImages.length) % allActiveImages.length);
  };

  const showNextPreview = () => {
    if (previewIndex === null || allActiveImages.length <= 1) return;
    setPreviewIndex((previewIndex + 1) % allActiveImages.length);
  };

  return (
    <div className="mt-4 p-3 w-100 overflow-hidden">
      {totalImages > 0 ? (
        <>
          <div
            className="w-100 overflow-hidden"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(activeImages.length, 1)}, minmax(0, 1fr))`,
              gap: '0.75rem',
            }}
          >
            {activeImages.map((image, index) => (
              <button
                type="button"
                className="shadow-sm border rounded text-center p-0 bg-light"
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  maxWidth: '100px',
                  maxHeight: '100px',
                  justifySelf: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                key={`${image?.image_url || 'image'}-${index}`}
                onClick={() => openPreview(image)}
                aria-label={`View image ${index + 1}`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '';
                }}
              >
                <img
                  src={image?.image_url}
                  alt={image.image_position || `Image ${index + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                  }}
                />
              </button>
            ))}
          </div>

          <div className="text-center mt-5">
            <p className="mb-2 text-secondary fw-bold">
              Showing {startIndex + 1}–{endIndex} of {totalImages}
            </p>
            <div className="d-flex justify-content-center align-items-center gap-3">
              {currentPage > 0 && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handlePrevPage}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}
              {endIndex < totalImages && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleNextPage}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              )}
            </div>
          </div>

          <Modal
            show={previewIndex !== null}
            onHide={closePreview}
            centered
            size="lg"
            dialogClassName="product-image-preview-modal"
          >
            <Modal.Body className="p-3 position-relative text-center bg-dark">
              <button
                type="button"
                className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
                onClick={closePreview}
                aria-label="Close image preview"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

              {allActiveImages.length > 1 && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light position-absolute top-50 start-0 translate-middle-y ms-2"
                    onClick={showPreviousPreview}
                    aria-label="Previous image"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-2"
                    onClick={showNextPreview}
                    aria-label="Next image"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </>
              )}

              {previewImage && (
                <>
                  <img
                    src={previewImage.image_url}
                    alt={previewImage.image_position || `Image ${(previewIndex ?? 0) + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '75vh',
                      objectFit: 'contain',
                    }}
                  />
                  <p className="text-white mb-0 mt-3 small">
                    {(previewIndex ?? 0) + 1} of {allActiveImages.length}
                    {previewImage.image_position ? ` · ${previewImage.image_position}` : ''}
                  </p>
                </>
              )}
            </Modal.Body>
          </Modal>
        </>
      ) : (
        <p className="text-center">No images available</p>
      )}
    </div>
  );
};

export default ImageGallery;
